import { ColumnVisibilityState } from '@tanstack/react-table';
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

interface UseColumnGroupToggleOptions {
  /** Column IDs that belong to this toggle group */
  columnIds: string[];
  /** Initial visibility state (default: true) */
  initialVisible?: boolean;
}

export interface UseColumnGroupToggleReturn {
  /** Whether the column group is currently visible */
  visible: boolean;
  /** Toggle the column group visibility */
  toggle: () => void;
  /** Current column visibility state (for useReactTable) */
  columnVisibility: ColumnVisibilityState;
  /** Setter for column visibility (for useReactTable) */
  setColumnVisibility: Dispatch<SetStateAction<ColumnVisibilityState>>;
  /** Calculated bracket width for visual indicator */
  bracketWidth: number;
  /** Ref for the toggle button (bracket start) */
  buttonRef: RefObject<HTMLButtonElement | null>;
  /** Ref for the bracket end marker */
  endRef: RefObject<HTMLDivElement | null>;
}

/**
 * Manages a toggleable group of columns with bracket visual indicator.
 * Pass columnVisibility and setColumnVisibility to useReactTable state.
 */
export const useColumnGroupToggle = ({
  columnIds,
  initialVisible = true
}: UseColumnGroupToggleOptions): UseColumnGroupToggleReturn => {
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => {
    if (initialVisible) {
      return {};
    }
    // Hide all columns in the group
    const hidden: ColumnVisibilityState = {};
    columnIds.forEach(col => {
      hidden[col] = false;
    });
    return hidden;
  });

  const [bracketWidth, setBracketWidth] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const visible = columnIds.every(col => columnVisibility[col] !== false);

  const toggle = useCallback(() => {
    const newVisibility: ColumnVisibilityState = {};
    columnIds.forEach(col => {
      newVisibility[col] = !visible;
    });
    setColumnVisibility(prev => ({ ...prev, ...newVisibility }));
  }, [columnIds, visible]);

  // Measure bracket width when visible and on resize
  useLayoutEffect(() => {
    const updateBracketWidth = () => {
      if (visible && buttonRef.current && endRef.current) {
        const btnRect = buttonRef.current.getBoundingClientRect();
        const endRect = endRef.current.getBoundingClientRect();
        const width = endRect.left + endRect.width / 2 - (btnRect.left + btnRect.width / 2);
        setBracketWidth(width);
      } else {
        setBracketWidth(0);
      }
    };

    updateBracketWidth();
    window.addEventListener('resize', updateBracketWidth);
    return () => window.removeEventListener('resize', updateBracketWidth);
  }, [visible, columnVisibility]);

  return {
    visible,
    toggle,
    columnVisibility,
    setColumnVisibility,
    bracketWidth,
    buttonRef,
    endRef
  };
};
